// BrokerDealerComponents.js — InvestorQ Private Market
// Frontend React components ONLY — no Node.js, no backend code
// File: src/components/BrokerDealerComponents.js
// OffBeet Media & Tech Inc. · investorq.io

import { useState, useEffect, useRef } from 'react';
import { PreInvestmentWarning, RaiseDisclaimer } from './ComplianceDisclosures';

const API_URL = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';

// ─────────────────────────────────────────────────────────────────────────────
// DealMakerCheckoutButton
// Replaces the static "INVEST VIA DEALMAKER" button.
// Phases: idle → warning → loading → embedded (iframe) or error
// ─────────────────────────────────────────────────────────────────────────────
export function DealMakerCheckoutButton({
  dealId,
  raise,
  investorEmail = '',
  investorFirstName = '',
  investorLastName = '',
  mode = 'redirect',
}) {
  const [phase, setPhase]     = useState('idle');
  const [accessLink, setLink] = useState(null);
  const [error, setError]     = useState(null);
  const iframeRef             = useRef(null);

  const initiateCheckout = async () => {
    setPhase('loading');
    setError(null);
    try {
      const res = await fetch(`${API_URL}/raises/dealmaker/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dealId, investorEmail, investorFirstName, investorLastName }),
      });
      const data = await res.json();
      if (!res.ok || !data.accessLink) throw new Error(data.error || 'Checkout initiation failed');

      if (mode === 'embed') {
        setLink(data.accessLink);
        setPhase('embedded');
      } else {
        window.open(data.accessLink, '_blank', 'noopener,noreferrer');
        setPhase('idle');
      }
    } catch (err) {
      setError(err.message);
      setPhase('error');
    }
  };

  const directLink = raise?.raise_url || `https://app.dealmaker.tech/deals/${dealId}`;

  if (phase === 'warning') {
    return (
      <PreInvestmentWarning
        platform={raise?.platform || 'DealMaker'}
        onContinue={() => {
          if (investorEmail) {
            initiateCheckout();
          } else {
            window.open(directLink, '_blank', 'noopener,noreferrer');
            setPhase('idle');
          }
        }}
      />
    );
  }

  if (phase === 'embedded' && accessLink) {
    return (
      <div>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
          <div style={{ fontSize:11, fontWeight:800, color:'#f5a623' }}>
            ◆ DealMaker Checkout — {raise?.name}
          </div>
          <button onClick={() => setPhase('idle')} style={{
            background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.1)',
            color:'#94a3b8', padding:'4px 10px', borderRadius:8, fontSize:11, cursor:'pointer',
          }}>✕ Close</button>
        </div>
        <div style={{ border:'1px solid rgba(245,166,35,0.3)', borderRadius:12, overflow:'hidden', background:'#fff' }}>
          <iframe
            ref={iframeRef}
            src={accessLink}
            style={{ width:'100%', height:520, border:'none', display:'block' }}
            allow="payment"
            title="DealMaker Investment Checkout"
            sandbox="allow-forms allow-scripts allow-same-origin allow-popups allow-top-navigation"
          />
        </div>
        <RaiseDisclaimer platform="DealMaker" raiseType={raise?.type || 'Reg CF'} compact />
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div>
        <div style={{ background:'rgba(248,113,113,0.08)', border:'1px solid rgba(248,113,113,0.2)',
          borderRadius:10, padding:'12px 14px', marginBottom:10, fontSize:11, color:'#f87171' }}>
          ⚠ Checkout initiation failed. Opening DealMaker directly.
        </div>
        <button onClick={() => { window.open(directLink, '_blank'); setPhase('idle'); }} style={{
          width:'100%', padding:12, borderRadius:12, border:'none', cursor:'pointer',
          background:'linear-gradient(135deg, #f5a623, #ffc75f)', color:'#000',
          fontSize:12, fontWeight:900, letterSpacing:'0.06em',
        }}>
          Continue on DealMaker →
        </button>
      </div>
    );
  }

  if (phase === 'loading') {
    return (
      <button disabled style={{
        width:'100%', padding:13, borderRadius:12, border:'none',
        background:'rgba(245,166,35,0.3)', color:'#f5a623',
        fontSize:12, fontWeight:900, letterSpacing:'0.06em', cursor:'not-allowed',
      }}>
        Preparing checkout…
      </button>
    );
  }

  return (
    <div>
      <button
        onClick={() => setPhase('warning')}
        style={{
          width:'100%', padding:13, borderRadius:12, border:'none', cursor:'pointer',
          background:'linear-gradient(135deg, #f5a623, #ffc75f)', color:'#000',
          fontSize:12, fontWeight:900, letterSpacing:'0.06em',
          boxShadow:'0 8px 32px rgba(245,166,35,0.4)', transition:'all 0.2s',
        }}
        onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-1px)'}
        onMouseLeave={e => e.currentTarget.style.transform = 'none'}
      >
        ◆ INVEST VIA {(raise?.platform || 'DealMaker').toUpperCase()} →
      </button>
      <RaiseDisclaimer
        platform={raise?.platform || 'DealMaker'}
        raiseType={raise?.type || 'Reg CF'}
        compact
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LiveRaiseProgressSync
// Polls backend /api/raises/:dealId/stats every 5 minutes.
// Falls back to initialRaise data if API is unavailable.
// ─────────────────────────────────────────────────────────────────────────────
export function LiveRaiseProgressSync({ dealId, initialRaise, onUpdate }) {
  const [stats,    setStats]    = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [lastSync, setLastSync] = useState(null);

  const syncStats = async () => {
    if (!dealId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/raises/${dealId}/stats`);
      if (!res.ok) throw new Error('Sync failed');
      const data = await res.json();
      setStats(data);
      setLastSync(new Date());
      if (onUpdate) onUpdate(data);
    } catch (err) {
      console.warn('Raise stats sync failed:', err.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    syncStats();
    const interval = setInterval(syncStats, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [dealId]);

  const displayRaised = stats?.raised  ?? initialRaise?.raised  ?? 0;
  const displayTarget = stats?.target  ?? initialRaise?.target  ?? 1;
  const displayPct    = stats?.percentFunded ?? Math.min(100, Math.round(displayRaised / displayTarget * 100));
  const investorCount = stats?.investorCount ?? null;

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
        <span style={{ fontSize:9, color:'#475569', textTransform:'uppercase', letterSpacing:'0.1em' }}>
          {loading ? 'Syncing…' : 'Live Raise Progress'}
          {lastSync && !loading && (
            <span style={{ color:'#334155', marginLeft:6 }}>
              · synced {lastSync.toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' })}
            </span>
          )}
        </span>
        <button onClick={syncStats} disabled={loading} style={{
          background:'transparent', border:'none', cursor:'pointer', color:'#475569', fontSize:10, padding:0,
        }}>↻</button>
      </div>
      <div style={{ height:10, background:'rgba(255,255,255,0.06)', borderRadius:5, marginBottom:8, overflow:'hidden' }}>
        <div style={{
          width:`${displayPct}%`, height:'100%',
          background:'linear-gradient(90deg, #f5a623, #ffc75f)', borderRadius:5,
          boxShadow:'0 0 14px rgba(245,166,35,0.6)', transition:'width 1.2s ease',
        }} />
      </div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <span style={{ fontSize:12, color:'#94a3b8' }}>
          ${displayRaised.toLocaleString()} raised
          {investorCount !== null && ` · ${investorCount.toLocaleString()} investors`}
        </span>
        <span style={{ fontSize:12, fontWeight:800, color:'#f5a623' }}>
          {displayPct}% of ${displayTarget.toLocaleString()}
        </span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RepublicEmbedWidget
// Iframes the Republic campaign page once republic_campaign_url is set.
// Contact partnerships@republic.com for co-branded embed approval.
// ─────────────────────────────────────────────────────────────────────────────
export function RepublicEmbedWidget({ campaignUrl, raise }) {
  const [phase, setPhase] = useState('idle');

  if (!campaignUrl) {
    return (
      <div style={{ textAlign:'center', padding:'20px', color:'#64748b', fontSize:11 }}>
        Republic campaign URL not configured yet. Contact partnerships@republic.com.
      </div>
    );
  }

  if (phase === 'warning') {
    return (
      <PreInvestmentWarning
        platform="Republic"
        onContinue={() => { window.open(campaignUrl, '_blank', 'noopener,noreferrer'); setPhase('idle'); }}
      />
    );
  }

  return (
    <div>
      <button onClick={() => setPhase('warning')} style={{
        width:'100%', padding:13, borderRadius:12, border:'none', cursor:'pointer',
        background:'linear-gradient(135deg, #f5a623, #ffc75f)', color:'#000',
        fontSize:12, fontWeight:900, letterSpacing:'0.06em',
        boxShadow:'0 8px 32px rgba(245,166,35,0.4)',
      }}>
        ◆ INVEST VIA REPUBLIC →
      </button>
      <RaiseDisclaimer platform="Republic" raiseType={raise?.type || 'Reg CF'} compact />
    </div>
  );
}